/**
 * Legal content for Velox Algo Tech.
 * Source: "what is Velox Algo Tech.docx" (as of April 10, 2026 — per user doc).
 * Each document is split into sections for readable, scannable rendering.
 */

const LAST_UPDATED = "April 10, 2026";

export const LEGAL_CONTENT = {
  terms: {
    title: "Terms of Use",
    lastUpdated: LAST_UPDATED,
    intro:
      "Welcome to Velox Algo Tech. By accessing our website (veloxalgotech.com) or mobile application, you agree to these Terms of Use. The Services are operated by Velox Algotech (commercial name: Velox Algo AI). Our Services are intended for use by fully competent adults only.",
    sections: [
      {
        heading: "1. Governing Law",
        body: "These Terms are governed by the laws of the jurisdiction in which Velox Algotech is registered and operates. Any disputes shall be subject to the exclusive jurisdiction of the courts of that jurisdiction.",
      },
      {
        heading: "2. User Conduct",
        body: "You agree to use our website and Services only for lawful purposes, in a manner that does not harm the website, other users, or third parties. You will not attempt unauthorized access, introduce malicious code, or use automated tools (scrapers, bots) without our written permission.",
      },
      {
        heading: "3. Intellectual Property",
        body: "All content, source code, designs, trademarks, and materials on the website and app are owned by Velox Algotech or its licensors. You may not republish, sell, sub-license, copy, or redistribute any material without our prior written permission.",
      },
      {
        heading: "4. User Content",
        body: "By submitting any content (comments, configurations, feedback) to our Services, you grant Velox Algotech a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display that content for the purpose of operating and improving the Services. You warrant that you own the content and it does not infringe any third-party rights.",
      },
      {
        heading: "5. Hyperlinking",
        body: "Links to our site from government agencies, recognized search engines, and established news organizations are permitted. All other parties must obtain our written consent before linking to veloxalgotech.com.",
      },
      {
        heading: "6. Information Accuracy",
        body: "We do not warrant that the information provided on the Services is absolute, complete, or up-to-date. You should verify any information independently before relying on it for financial decisions.",
      },
      {
        heading: "7. Disclaimer of Warranties",
        body: 'The Services are provided "as is" and "as available" without warranties of any kind, whether express or implied, including merchantability, fitness for a particular purpose, or non-infringement.',
      },
      {
        heading: "8. Limitation of Liability",
        body: "To the fullest extent permitted by law, Velox Algotech shall not be liable for any loss or damage arising from your use of free information, Services, or any third-party content. For paid Services, liability is limited as detailed in the Services Agreement.",
      },
      {
        heading: "9. Contact",
        body: "For questions about these Terms, contact us via veloxalgotech.com or email support@veloxalgotech.com.",
      },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    lastUpdated: LAST_UPDATED,
    intro:
      "This Privacy Policy explains how Velox Algotech (operating as Velox Algo AI) collects, uses, and protects your personal data when you use our website and mobile application. We take your privacy seriously and follow strict measures to safeguard your information.",
    sections: [
      {
        heading: "1. Data We Collect",
        body: "• Identity Data — Name, professional details\n• Contact Data — Billing address, email, mobile number\n• Transaction Data — Subscriptions, purchases, trade management history\n• Technical Data — IP address, login data, browser, device information\n• Usage Data — How you interact with our website and app",
      },
      {
        heading: "2. How We Collect It",
        body: "We collect data through: account registration, direct interactions (contact forms, email, phone), and automated technologies (cookies, server logs, analytics scripts).",
      },
      {
        heading: "3. Cookies & Web Beacons",
        body: "We use cookies to remember your preferences and optimize your experience. You can configure your browser to refuse cookies, though some parts of the Services may not function correctly without them.",
      },
      {
        heading: "4. How We Use Your Data",
        body: "We use your data to: perform our contract with you, fulfill legitimate business interests (service improvement, security), and comply with legal obligations.",
      },
      {
        heading: "5. Marketing",
        body: "We may send marketing communications where we have an ongoing business relationship with you. For sharing with third parties for marketing purposes, we obtain your express consent. You can opt out of marketing at any time.",
      },
      {
        heading: "6. Disclosure of Personal Data",
        body: "We disclose your data only to: (a) external service providers and brokers we integrate with (e.g., Exness, XM, Doto) to deliver trading Services; (b) affiliated entities providing support and operational services; (c) authorities where legally required.",
      },
      {
        heading: "7. Data Security",
        body: "We implement strict security measures including encryption, access controls, and monitoring. Access to personal data is limited to authorized personnel who are bound by confidentiality obligations.",
      },
      {
        heading: "8. Your Legal Rights",
        body: "Depending on your jurisdiction (e.g., GDPR), you have rights to: access, rectify, or erase your data; object to or restrict processing; request data portability; and withdraw consent.",
      },
      {
        heading: "9. Account & Data Deletion",
        body: "You can delete your account via the mobile app settings. Some data may be retained for legal compliance, dispute resolution, and contractual obligations.",
      },
      {
        heading: "10. Contact",
        body: "For privacy questions or to exercise your rights, contact us at https://veloxalgotech.com/contactus or email support@veloxalgotech.com.",
      },
    ],
  },

  services: {
    title: "Services Agreement",
    lastUpdated: LAST_UPDATED,
    intro:
      "This Services Agreement governs your use of the Velox Algo AI application and related Services. By using the Services, you unconditionally accept this Agreement and our Privacy Policy.",
    sections: [
      {
        heading: "1. Definitions",
        body: "• App — The Velox Algo AI mobile and web application\n• Services — All features including bot automation, charts, AI presets, and related tools\n• Authorization Credentials — API keys used to connect your broker account",
      },
      {
        heading: "2. General",
        body: "Use of the Services requires an active third-party brokerage account. Velox Algo AI provides trading instructions but does not execute trades directly on your capital. We reserve the right to suspend or terminate the Services at our discretion.",
      },
      {
        heading: "3. Registration & Personal Account",
        body: "You must provide valid information during registration. The account is for your personal use only. You are responsible for the confidentiality of your credentials and all activity under your account.",
      },
      {
        heading: "4. Subscription & Paid Functionality",
        body: "Basic features are free. Advanced automation requires a subscription (monthly or annual). Subscriptions auto-renew unless cancelled. Access begins immediately upon payment.",
      },
      {
        heading: "5. Operational Modes",
        body: "• Demo (Not Activated) — Limited to demo trading with $10,000 virtual funds, standard support, and simulated profit tracking.\n• Live (Activated) — Real-account trading after connecting a platform and meeting broker deposit requirements. Includes unlimited profit tracking and high-priority support.",
      },
      {
        heading: "6. User Obligations & Restrictions",
        body: "You will not: scrape or reverse-engineer the Services, send unsolicited advertising, upload viruses, violate local laws, or share your login credentials.",
      },
      {
        heading: "7. Responsibility & Disclaimer",
        body: 'The Services are provided "as is" without warranties. We are not liable for actions of third parties (brokers, exchanges, payment providers), nor for trading losses or lost profits.',
      },
      {
        heading: "8. Risk Disclosure",
        body: "Trading involves significant risk of loss. Only invest what you can afford to lose. Velox Algo AI does not provide investment, legal, or tax advice. Always seek professional financial advice before trading.",
      },
      {
        heading: "9. Limitation of Liability",
        body: "Our total liability under this Agreement is limited to the lesser of USD 1,000 or the total amount you have paid us in the past six months — except in cases of gross negligence.",
      },
      {
        heading: "10. Force Majeure",
        body: "Neither party is liable for failures caused by events beyond reasonable control (natural disasters, internet outages, government action). Either party may terminate the Agreement if such conditions persist for more than 3 months.",
      },
      {
        heading: "11. Governing Law",
        body: "This Agreement is governed by the laws of the jurisdiction where Velox Algotech is registered.",
      },
      {
        heading: "12. Contact & Support",
        body: "For support, visit veloxalgotech.com/support or email support@veloxalgotech.com. We resolve complaints within 30 days.",
      },
    ],
  },

  refund: {
    title: "Refund Policy",
    lastUpdated: LAST_UPDATED,
    intro:
      "This Refund Policy explains when and how you can request a refund for payments made to Velox Algo AI. Note: the ₹2,999 platform joining fee is non-refundable once the account has been created.",
    sections: [
      {
        heading: "1. Platform-Specific Payments",
        body: "• Apple App Store — Contact Apple directly for refund requests.\n• Google Play Store — Contact Google directly for refund requests.\n• Direct Payments (Stripe / Razorpay) — Contact our support team at support@veloxalgotech.com.",
      },
      {
        heading: "2. General Rules",
        body: "• Refund can be utilized only once per customer.\n• Cryptocurrency payments are non-refundable.\n• Subscription cancellation must be done at least 24 hours before renewal.\n• Deleting the app does not automatically cancel an active subscription.\n• We may offer discounts, extended periods, or plan upgrades as alternative compensation in lieu of a cash refund.",
      },
      {
        heading: "3. Monthly Subscription",
        body: "• 14-Day Window — Full refund available within 14 days of the most recent payment.\n• After 14 Days — No refunds, except in cases of verified technical issues on our end.",
      },
      {
        heading: "4. Annual Subscription",
        body: "• Cooling-Off Period — Full refund within the first 14 days of purchase.\n• Partial Refund — Within the first 180 days (6 months), a partial refund is available for the unused portion. Calculation: Total payment minus the pro-rata cost of months used.\n• After 180 Days — No refunds available.",
      },
      {
        heading: "5. Processing & Fees",
        body: "• Currency — Refunds are issued in the original payment currency or its equivalent at current rates.\n• Timeline — Refunds are usually processed within 10 business days of approval.\n• Third-Party Fees — Bank or payment provider fees are not refunded by Velox Algotech.\n• VAT — Not refundable for periods already used.",
      },
      {
        heading: "6. How to Request a Refund",
        body: "Email support@veloxalgotech.com with: your account email, payment date, order/transaction ID, and reason for the refund request. Our team responds within 2 business days.",
      },
    ],
  },
};

export const LEGAL_DISCLAIMER = {
  title: "Important Disclaimer",
  body: [
    "Velox Algotech operates as an unregulated software development entity.",
    "Trading involves inherent financial risks; historical performance does not guarantee future outcomes.",
    "Profit figures shown in marketing materials are illustrative and may not reflect actual results.",
    "Only individuals with sufficient market expertise should consider bot trading. Seek professional financial advice if needed.",
    "We explicitly disclaim liability for any losses or damages arising from the use of the software — including market volatility, settings misconfiguration, or third-party brokerage actions.",
    "By using the Services, you acknowledge these risks and agree to indemnify Velox Algotech from any liabilities or losses.",
  ],
};
